import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { assertRenderableChildDeep, debugEnabled } from "@/lib/react/assertRenderableChild";
import { safeNode } from "@/lib/renderSafe";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-[var(--duration-standard)] ease-[var(--ease-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] shadow-sm hover:shadow-md border border-primary/20",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow-md border border-destructive/20",
        outline: "border border-border bg-transparent hover:bg-accent/40 hover:border-border/80 text-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm border border-border/40",
        ghost: "hover:bg-accent/40 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-primary text-primary-foreground shadow-md hover:shadow-glow hover:bg-primary/90 active:scale-[0.98] font-semibold border border-primary/20",
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-sm hover:shadow-md border border-success/20",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 shadow-sm hover:shadow-md border border-warning/20",
        glass: "bg-background/85 backdrop-blur-xl border border-border/40 text-foreground hover:bg-background/95",
        action: "bg-primary/10 text-primary border border-primary/20 hover:bg-primary hover:text-primary-foreground",
        landing: "bg-primary text-primary-foreground hover:bg-primary/95 active:scale-[0.98] shadow-[0_4px_12px_rgba(59,130,246,0.25)] hover:shadow-[0_6px_20px_rgba(59,130,246,0.35)] border border-primary/30 font-semibold tracking-[-0.01em] transition-all duration-[var(--duration-standard)] ease-[var(--ease-hover)]",
        landingOutline: "border border-border/50 bg-transparent hover:bg-accent/30 hover:border-border/70 text-foreground font-medium tracking-[-0.01em] transition-all duration-[var(--duration-standard)] ease-[var(--ease-hover)]",
        contrast: "bg-white text-black hover:bg-gray-100 active:scale-[0.98] shadow-sm hover:shadow-md border border-gray-200/50 focus-visible:ring-2 focus-visible:ring-gray-400",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-[13px]",
        lg: "h-11 rounded-lg px-8 text-base",
        xl: "h-12 rounded-lg px-10 text-base font-semibold",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    // Debug tripwire: catch React error #31 (object rendered as child) in development
    if (debugEnabled() && children !== undefined) {
      try {
        assertRenderableChildDeep(children, "Button", "Button", 0);
      } catch (error) {
        console.error("[Button] Invalid child detected:", error);
        // Re-throw to see full stack trace
        throw error;
      }
    }

    const Comp = asChild ? Slot : "button";
    // Sanitize children to prevent React error #31 (objects rendered as children)
    const safeChildren = safeNode(children);
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>{safeChildren}</Comp>;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
